import Modal from "../../../../../../components/base/Modal";
import type { ThemeClasses, TripFormData, TripAssignmentFormData, TimezoneCode } from "../types";
import { TIMEZONE_OPTIONS } from "../types";
import type { UseFormRegister, FieldErrors, UseFormSetValue } from "react-hook-form";
import type { TFunction } from "i18next";
import { Combobox, DateTimeRange, SwitchTrack } from "../../../../../../components/ui";
import { useRoutesStore } from "../../../../../../store/admin/routesStore";
import { useUsersStore } from "../../../../../../store/admin/usersStore";
import { useAllDevices } from "../../../../../../features/devices/queries";
import { useStatuses } from "../../../../../../features/catalogs/queries";
import { RouteSummary } from "./RouteSummary";
import { TripShipmentsSection } from "./TripShipmentsSection";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatAssignmentRange } from "../utils/assignmentFormat";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { useIsReseller } from "../../../../../../hooks/useIsReseller";
import { useCanSeeDevicePairing } from "../../../../../../hooks/useCanSeeDevicePairing";
import { buildAssignmentDeviceOptions } from "../utils/deviceOptions";
import { hasSecondaryDevices } from "../../../../../../features/devices/pairing";

interface TripAddModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  register: UseFormRegister<TripFormData>;
  errors: FieldErrors<TripFormData>;
  loadingSubmit: boolean;
  onClose: () => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  t: TFunction;
  setValue: UseFormSetValue<TripFormData>;
  watchedValues: Partial<TripFormData>;
  showShipments?: boolean;
  hideNotificationFields?: boolean;
}

export function TripAddModal({
  themeClasses,
  isDarkMode,
  register,
  errors,
  loadingSubmit,
  onClose,
  onSubmit,
  t,
  setValue,
  watchedValues,
  showShipments = false,
  hideNotificationFields = false,
}: TripAddModalProps) {
  const inputClass = `w-full min-h-10 px-3 py-2 text-xs bg-app border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`;
  const labelClass = `block text-sm font-medium ${themeClasses.text} mb-1`;

  const routes = useRoutesStore((state) => state.routes || []);
  const fetchRoutes = useRoutesStore((state) => state.fetchRoutes);

  const { data: devicesList = [] } = useAllDevices();
  const { usersLight: usersList, fetchUsersLight } = useUsersStore();
  const { data: statuses = [] } = useStatuses();
  const { isReseller } = useIsReseller();
  const showPairing = useCanSeeDevicePairing();

  const [assignments, setAssignments] = useState<TripAssignmentFormData[]>([]);
  const [newAssignment, setNewAssignment] = useState<Partial<TripAssignmentFormData>>({
    device_id: "",
    start_time: "",
    end_time: "",
    timezone: "America/Mexico_City",
  });

  // Timezone options for select
  const timezoneOptions = Object.entries(TIMEZONE_OPTIONS).map(([value, label]) => ({
    value: value as TimezoneCode,
    label,
  }));

  useEffect(() => {
    fetchRoutes({ limit: 500, page: 1 });
    // Resellers can assign carriers from any tenant; bypass the company filter
    // so the dropdown lists every user instead of just the active company.
    fetchUsersLight({ allCompanies: isReseller });
  }, [fetchRoutes, fetchUsersLight, isReseller]);

  // Get status options for trips entity
  const statusOptions = getStatusOptionsForEntity(statuses, "trips", t);

  useEffect(() => {
    setValue("assignments", assignments);
  }, [assignments, setValue]);

  const selectedRoute = routes.find((r) => r.id === watchedValues.route_id) || null;

  const deviceOptions = buildAssignmentDeviceOptions(devicesList, {
    showPairing,
    primaryLabel: t("trips.assignments.primaryTag"),
    secondaryLabel: t("trips.assignments.secondaryTag"),
  });

  const userOptions = usersList.map((u) => ({
    value: u.id,
    label: `${u.first_name} ${u.last_name} (${u.email})`,
  }));

  const routeOptions = routes.map((r: { id: string; name?: string }) => ({
    value: r.id,
    label: r.name || r.id,
  }));

  const priorityOptions = [
    { value: "low", label: t("trips.priority.low") },
    { value: "medium", label: t("trips.priority.medium") },
    { value: "high", label: t("trips.priority.high") },
    { value: "urgent", label: t("trips.priority.urgent") },
  ];

  const handleAddAssignment = () => {
    if (!newAssignment.device_id || !newAssignment.start_time || !newAssignment.end_time) {
      return;
    }

    // Extract date from start_time (format: YYYY-MM-DDTHH:mm)
    const date = newAssignment.start_time.split('T')[0] || "";

    const assignment: TripAssignmentFormData = {
      device_id: newAssignment.device_id,
      date: date,
      start_time: newAssignment.start_time,
      end_time: newAssignment.end_time,
      status: "pending",
      timezone: newAssignment.timezone || "America/Mexico_City",
    };

    setAssignments([...assignments, assignment]);
    setNewAssignment({
      device_id: "",
      start_time: "",
      end_time: "",
      timezone: "America/Mexico_City",
    });
  };

  const handleRemoveAssignment = (index: number) => {
    setAssignments(assignments.filter((_, i) => i !== index));
  };

  const getDeviceLabel = (deviceId: string) => {
    const device = devicesList.find((d) => d.id === deviceId);
    if (!device) return deviceId;
    const vehicleName = device.vehicle ? device.vehicle?.name : "";
    const trailerName = device.remolque ? device.remolque?.name : "";
    return `${device.name || device.identifier} ${vehicleName ? `(${vehicleName})` : ""} ${trailerName ? `(${trailerName})` : ""}`.trim();
  };

  const { i18n } = useTranslation();
  const locale = i18n.language || "es";
  const assignmentRange = (start?: string, end?: string) => {
    const { range, duration } = formatAssignmentRange(start, end, locale);
    return duration ? `${range} · ${duration}` : range;
  };

  return (
    <Modal
      title={t("trips.modal.addTitle")}
      onClose={onClose}
      themeClasses={themeClasses}
      width="w-full max-w-4xl"
      content={
        <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto px-2">
          {/* Basic Info */}
          <h3 className={`text-sm font-semibold ${themeClasses.text}`}>
            {t("trips.sections.basicInfo")}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>{t("trips.fields.title")} <span className="text-error">*</span></label>
              <input
                {...register("title", { required: t("trips.validation.titleRequired") })}
                className={`${inputClass} ${errors.title ? "border-error/30" : ""}`}
                placeholder={t("trips.fields.title")}
              />
              {errors.title && (
                <span className="text-error text-xs">{errors.title.message as string}</span>
              )}
            </div>
            <div>
              <label className={labelClass}>{t("trips.fields.status")} <span className="text-error">*</span></label>
              <input type="hidden" {...register("status_id", { required: t("trips.validation.statusRequired") })} />
              <Combobox
                options={statusOptions}
                placeholder={t("trips.modal.selectStatus")}
                value={statusOptions.find((opt) => opt.value === watchedValues.status_id) || null}
                onChange={(option: any) => setValue("status_id", option?.value || "", { shouldValidate: true })}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {errors.status_id && (
                <span className="text-error text-xs">{errors.status_id.message as string}</span>
              )}
            </div>
            <div>
              <label className={labelClass}>{t("trips.fields.priority")} <span className="text-error">*</span></label>
              <input type="hidden" {...register("priority", { required: t("trips.validation.priorityRequired") })} />
              <Combobox
                options={priorityOptions}
                placeholder={t("trips.modal.selectPriority")}
                value={priorityOptions.find((opt) => opt.value === watchedValues.priority) || null}
                onChange={(option: any) => setValue("priority", option?.value || "", { shouldValidate: true })}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {errors.priority && (
                <span className="text-error text-xs">{errors.priority.message as string}</span>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>{t("trips.fields.description")}</label>
            <textarea
              {...register("description")}
              className={`${inputClass} min-h-[80px]`}
              placeholder={t("trips.fields.description")}
            />
          </div>

          {/* Route Info */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} mt-6`}>
            {t("trips.sections.routeInfo")}
          </h3>

          <div>
            <label className={labelClass}>{t("trips.fields.route")} <span className="text-error">*</span></label>
            <input type="hidden" {...register("route_id", { required: t("trips.validation.routeRequired") })} />
            <Combobox
              options={routeOptions}
              placeholder={t("trips.modal.selectRoute")}
              value={routeOptions.find((opt: { value: string }) => opt.value === watchedValues.route_id) || null}
              onChange={(option: any) => setValue("route_id", option?.value || "", { shouldValidate: true })}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {errors.route_id && (
              <span className="text-error text-xs">{errors.route_id.message as string}</span>
            )}
            {selectedRoute && (
              <RouteSummary
                route={selectedRoute}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                t={t}
              />
            )}
          </div>

          {/* Device Assignments */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} mt-6`}>
            {t("trips.assignments.title")}
          </h3>

          {/* Add new assignment - compact layout */}
          <div className={`p-3 rounded-lg border ${themeClasses.cardBorder} bg-surface`}>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:items-end">
              {/* Device */}
              <div className="sm:col-span-3">
                <label className={`text-xs ${themeClasses.textSecondary} mb-1 block`}>{t("trips.assignments.device")}</label>
                <Combobox
                  options={deviceOptions}
                  placeholder={t("trips.assignments.selectDevice")}
                  value={deviceOptions.find((opt) => opt.value === newAssignment.device_id) || null}
                  onChange={(option: any) => setNewAssignment({ ...newAssignment, device_id: option?.value || "" })}
                  isSearchable
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {!showPairing && newAssignment.device_id && hasSecondaryDevices(devicesList, newAssignment.device_id) && (
                  <p className="mt-1 text-xs text-content-muted">{t("trips.assignments.hasSecondaries")}</p>
                )}
              </div>
              {/* Schedule */}
              <div className="sm:col-span-4">
                <label className={`text-xs ${themeClasses.textSecondary} mb-1 block`}>{t("trips.assignments.schedule")}</label>
                <DateTimeRange
                  locale={locale}
                  labels={{
                    placeholder: t("trips.assignments.pickDates"),
                    startTime: t("trips.assignments.timeStart"),
                    endTime: t("trips.assignments.timeEnd"),
                    done: t("trips.assignments.done"),
                    previousMonth: t("trips.assignments.previousMonth"),
                    nextMonth: t("trips.assignments.nextMonth"),
                    invalidRange: t("trips.assignments.invalidRange"),
                  }}
                  value={{ start: newAssignment.start_time || "", end: newAssignment.end_time || "" }}
                  onChange={({ start, end }) => setNewAssignment((current) => ({ ...current, start_time: start, end_time: end }))}
                />
              </div>
              {/* Timezone */}
              <div className="sm:col-span-3">
                <label className={`text-xs ${themeClasses.textSecondary} mb-1 block`}>{t("trips.assignments.timezone")}</label>
                <Combobox
                  options={timezoneOptions}
                  placeholder={t("trips.assignments.selectTimezone")}
                  value={timezoneOptions.find((opt) => opt.value === newAssignment.timezone) || timezoneOptions.find((opt) => opt.value === "America/Mexico_City")}
                  onChange={(option: any) => setNewAssignment({ ...newAssignment, timezone: option?.value || "America/Mexico_City" })}
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              {newAssignment.start_time && newAssignment.end_time && (
                <p className={`sm:col-span-12 text-xs ${themeClasses.textSecondary}`}>
                  <i className="ri-calendar-event-line mr-1" />
                  {assignmentRange(newAssignment.start_time, newAssignment.end_time)}
                </p>
              )}
              {/* Button */}
              <div className="sm:col-span-12 flex sm:justify-end">
                <button
                  type="button"
                  onClick={handleAddAssignment}
                  disabled={!newAssignment.device_id || !newAssignment.start_time || !newAssignment.end_time || newAssignment.end_time <= newAssignment.start_time}
                  className="h-9 px-5 text-sm rounded-full font-medium bg-primary-500 hover:bg-primary-400 text-on-primary inline-flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                >
                  <i className="ri-add-line" />
                  {t("trips.assignments.add")}
                </button>
              </div>
            </div>
          </div>

          {/* Assignments list - compact cards */}
          {assignments.length > 0 && (
            <div className="space-y-2">
              {assignments.map((assignment, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-2 rounded-lg border ${themeClasses.cardBorder} bg-surface`}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${themeClasses.text} truncate`}>
                      {getDeviceLabel(assignment.device_id)}
                    </p>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      {assignmentRange(assignment.start_time, assignment.end_time)}
                      {assignment.timezone && (
                        <span className="ml-2 text-primary-500">
                          <i className="ri-time-zone-line mr-0.5" />
                          {TIMEZONE_OPTIONS[assignment.timezone] || assignment.timezone}
                        </span>
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveAssignment(index)}
                    className="ml-2 p-1.5 text-error hover:bg-error/10 rounded transition-colors"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {showShipments && (
            <TripShipmentsSection
              value={watchedValues.shipment_ids}
              onChange={(ids) => setValue("shipment_ids", ids)}
              themeClasses={themeClasses}
              t={t}
            />
          )}

          {!hideNotificationFields && (
            <>
          {/* Notified Users */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} mt-6`}>
            {t("trips.sections.notifications")}
          </h3>

          <div className="flex flex-col gap-1.5">
            <label className={labelClass}>{t("trips.fields.notifiedUsers")}</label>
            <p className={`text-xs text-content-muted -mt-1 mb-1`}>
              {t("trips.fields.notifiedUsersHint")}
            </p>
            <Combobox
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              options={userOptions}
              placeholder={t("trips.modal.selectUsers")}
              value={((watchedValues.notifiedUsers as string[] | undefined) || []).map((val) => {
                const existing = userOptions.find((o) => o.value === val);
                return existing || { value: val, label: val };
              })}
              onChange={(selected) =>
                setValue(
                  "notifiedUsers",
                  selected ? selected.map((s) => s.value) : []
                )
              }
              isClearable
              isSearchable
              noOptionsMessage={() => t("trips.modal.noUsersAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Envío de correos además del push */}
          <div className="flex flex-col gap-2">
            <label className={labelClass}>{t("trips.fields.sendEmails")}</label>
            <p className={`text-xs text-content-muted -mt-1 mb-1`}>
              {t("trips.fields.sendEmailsHint")}
            </p>
            {(() => {
              const active = watchedValues.sendEmails === true;
              return (
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  onClick={() => setValue("sendEmails", !active)}
                  className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                    active
                      ? "bg-primary-500/10 border-primary-400 text-primary-600"
                      : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-mail-send-line 2xl:text-lg" aria-hidden />
                    {active
                      ? t("trips.fields.sendEmailsOn")
                      : t("trips.fields.sendEmailsOff")}
                  </span>
                  <SwitchTrack checked={active} size="sm" />
                </button>
              );
            })()}
          </div>
            </>
          )}

          {/* Comment */}
          <div>
            <label className={labelClass}>{t("trips.fields.comment")}</label>
            <textarea
              {...register("comment")}
              className={`${inputClass} min-h-[60px]`}
              placeholder={t("trips.fields.comment")}
            />
          </div>

          <div className="flex gap-3 border-t border-line pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 h-10 px-4 text-sm rounded-full ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} font-medium transition-colors inline-flex items-center justify-center gap-1.5`}
            >
              {t("trips.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={loadingSubmit}
              className="flex-1 h-10 px-4 text-sm rounded-full font-medium bg-primary-500 hover:bg-primary-400 text-on-primary inline-flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingSubmit ? t("trips.actions.creating") : t("trips.actions.create")}
            </button>
          </div>
        </form>
      }
    />
  );
}
